import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { registrationService } from '../../services/apiService';

const Container = styled.div`
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
`;

const Title = styled.h1`
  color: #333;
  margin-bottom: 2rem;
  border-bottom: 3px solid #667eea;
  padding-bottom: 1rem;
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  border-bottom: 2px solid #e0e0e0;
`;

const Tab = styled.button`
  padding: 1rem 2rem;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 600;
  color: ${props => props.active ? '#667eea' : '#999'};
  border-bottom: ${props => props.active ? '3px solid #667eea' : 'none'};
  transition: all 0.3s ease;
  margin-bottom: -2px;

  &:hover {
    color: #667eea;
  }
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const Card = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  border-left: 4px solid #667eea;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }

  h3 {
    color: #333;
    margin: 0 0 1rem 0;
    font-size: 1.2rem;
  }
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.8rem;
  font-size: 0.95rem;

  label {
    font-weight: 600;
    color: #666;
  }

  value {
    color: #333;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1.5rem;
  flex-wrap: wrap;
`;

const Button = styled.button`
  padding: 0.6rem 1.2rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  flex: 1;
  min-width: 120px;

  &.approve {
    background: #4caf50;
    color: white;

    &:hover {
      background: #45a049;
    }
  }

  &.reject {
    background: #f44336;
    color: white;

    &:hover {
      background: #da190b;
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 8px;
  max-width: 500px;
  width: 100%;

  h2 {
    color: #333;
    margin-top: 0;
  }

  textarea {
    width: 100%;
    min-height: 100px;
    padding: 0.75rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    margin: 1rem 0;
    font-family: inherit;
  }

  .actions {
    display: flex;
    gap: 1rem;
    margin-top: 1.5rem;

    button {
      flex: 1;
      padding: 0.75rem;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      font-weight: 600;

      &.confirm {
        background: #f44336;
        color: white;

        &:hover {
          background: #da190b;
        }
      }

      &.cancel {
        background: #ddd;
        color: #333;

        &:hover {
          background: #bbb;
        }
      }
    }
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  background: ${props => {
    switch (props.status) {
      case 'Pending':
        return '#fff3cd';
      case 'Approved':
        return '#d4edda';
      case 'Rejected':
        return '#f8d7da';
      default:
        return '#e2e3e5';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'Pending':
        return '#856404';
      case 'Approved':
        return '#155724';
      case 'Rejected':
        return '#721c24';
      default:
        return '#383d41';
    }
  }};
`;

const EmptyMessage = styled.p`
  text-align: center;
  color: #999;
  padding: 2rem;
  font-size: 1.1rem;
`;

const AdminRegistrationApprovals = () => {
  const [activeTab, setActiveTab] = useState('sellers');
  const [sellerRegs, setSellerRegs] = useState([]);
  const [buyerRegs, setBuyerRegs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rejectModal, setRejectModal] = useState({ open: false, id: null, type: null });
  const [rejectionReason, setRejectionReason] = useState('');
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  // Load both sellers and buyers on component mount
  useEffect(() => {
    fetchAllRegistrations();
  }, []);

  const fetchAllRegistrations = async () => {
    setLoading(true);
    setError(null);
    try {
      const [sellers, buyers] = await Promise.all([
        registrationService.getPendingSellerRegistrations(),
        registrationService.getPendingBuyerRegistrations()
      ]);
      setSellerRegs(sellers || []);
      setBuyerRegs(buyers || []);
    } catch (error) {
      console.error('Error fetching registrations:', error);
      setError('Failed to load registrations');
    }
    setLoading(false);
  };

  const handleApprove = async (id, type) => {
    try {
      setError(null);
      setActionLoading(`approve-${id}`);
      const adminId = parseInt(localStorage.getItem('userId')) || 1;
      
      if (type === 'seller') {
        const response = await fetch(`/api/registration/admin/approve-seller/${id}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
          body: JSON.stringify({ adminId })
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
      } else {
        const response = await fetch(`/api/registration/admin/approve-buyer/${id}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
          body: JSON.stringify({ adminId })
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
      }
      
      console.log(`✅ Successfully approved ${type} registration ID: ${id}`);
      // Refresh both lists
      await fetchAllRegistrations();
    } catch (error) {
      console.error(`❌ Error approving ${type} registration:`, error);
      setError(`Failed to approve ${type} registration: ${error.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectClick = (id, type) => {
    setRejectModal({ open: true, id, type });
    setRejectionReason('');
  };

  const handleRejectConfirm = async () => {
    try {
      setError(null);
      setActionLoading(`reject-${rejectModal.id}`);
      const { id, type } = rejectModal;
      console.log(`Rejecting ${type} registration ID: ${id}, Reason: ${rejectionReason}`);
      
      if (type === 'seller') {
        await registrationService.rejectSellerRegistration(id, rejectionReason);
      } else {
        await registrationService.rejectBuyerRegistration(id, rejectionReason);
      }
      
      console.log(`✅ Successfully rejected ${type} registration ID: ${id}`);
      setRejectModal({ open: false, id: null, type: null });
      setRejectionReason('');
      // Refresh both lists
      await fetchAllRegistrations();
    } catch (error) {
      console.error(`❌ Error rejecting ${rejectModal.type} registration:`, error);
      setError(`Failed to reject registration: ${error.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const registrations = activeTab === 'sellers' ? sellerRegs : buyerRegs;

  return (
    <Container>
      <Title>📋 Registration Approvals</Title>

      {error && (
        <div style={{ padding: '1rem', marginBottom: '1rem', background: '#fee', border: '1px solid #fcc', borderRadius: '4px', color: '#c00' }}>
          ⚠️ {error}
        </div>
      )}

      <TabsContainer>
        <Tab
          active={activeTab === 'sellers'}
          onClick={() => setActiveTab('sellers')}
        >
          👨‍🌾 Sellers ({sellerRegs.length})
        </Tab>
        <Tab
          active={activeTab === 'buyers'}
          onClick={() => setActiveTab('buyers')}
        >
          🏢 Buyers ({buyerRegs.length})
        </Tab>
      </TabsContainer>

      {loading ? (
        <EmptyMessage>⏳ Loading registrations...</EmptyMessage>
      ) : registrations.length === 0 ? (
        <EmptyMessage>✓ No pending registrations</EmptyMessage>
      ) : (
        <CardGrid>
          {registrations.map((reg) => (
            <Card key={reg.registrationId}>
              <h3>
                {activeTab === 'sellers' ? reg.sellerName : (reg.firstName && reg.lastName ? `${reg.firstName} ${reg.lastName}` : reg.buyerName)}
                <StatusBadge status={reg.status}>{reg.status}</StatusBadge>
              </h3>

              {activeTab === 'sellers' ? (
                <>
                  {/* Personal Info */}
                  <InfoRow><label>Phone:</label><value>{reg.phoneNumber || 'N/A'}</value></InfoRow>
                  <InfoRow><label>Email:</label><value>{reg.email || 'N/A'}</value></InfoRow>
                  
                  {/* Banking Info */}
                  {(reg.pan || reg.gstin || reg.bankAccountNumber) && (
                    <>
                      <InfoRow><label>PAN:</label><value>{reg.pan || 'N/A'}</value></InfoRow>
                      <InfoRow><label>GSTIN:</label><value>{reg.gstin || 'N/A'}</value></InfoRow>
                      <InfoRow><label>Bank Acc:</label><value>{reg.bankAccountNumber || 'N/A'}</value></InfoRow>
                      <InfoRow><label>IFSC:</label><value>{reg.ifscCode || 'N/A'}</value></InfoRow>
                    </>
                  )}
                  
                  {/* Location Info */}
                  {(reg.state || reg.region || reg.district) && (
                    <>
                      <InfoRow><label>State:</label><value>{reg.state || 'N/A'}</value></InfoRow>
                      <InfoRow><label>Region:</label><value>{reg.region || 'N/A'}</value></InfoRow>
                      <InfoRow><label>District:</label><value>{reg.district || 'N/A'}</value></InfoRow>
                      <InfoRow><label>Address:</label><value>{reg.addressLine1 || 'N/A'}</value></InfoRow>
                      {reg.addressLine2 && <InfoRow><label>Address 2:</label><value>{reg.addressLine2}</value></InfoRow>}
                      {reg.pincode && <InfoRow><label>Pincode:</label><value>{reg.pincode}</value></InfoRow>}
                    </>
                  )}
                  
                  {/* Farm Info */}
                  {(reg.farmName || reg.areaInAcres) && (
                    <>
                      {reg.farmName && <InfoRow><label>🚜 Farm:</label><value>{reg.farmName}</value></InfoRow>}
                      {reg.areaInAcres && <InfoRow><label>Area (Acres):</label><value>{reg.areaInAcres}</value></InfoRow>}
                      {reg.latitude && <InfoRow><label>Lat:</label><value>{reg.latitude}</value></InfoRow>}
                      {reg.longitude && <InfoRow><label>Lon:</label><value>{reg.longitude}</value></InfoRow>}
                    </>
                  )}
                  
                  {/* Crop Info */}
                  {(reg.primaryCropType || reg.cropDistribution) && (
                    <>
                      {reg.primaryCropType && <InfoRow><label>🌾 Primary Crop:</label><value>{reg.primaryCropType}</value></InfoRow>}
                      {reg.cropDistribution && <InfoRow><label>Crops:</label><value>{reg.cropDistribution}</value></InfoRow>}
                    </>
                  )}
                  
                  <InfoRow><label>Submitted:</label><value>{new Date(reg.createdAt).toLocaleDateString()}</value></InfoRow>
                </>
              ) : (
                <>
                  {/* Personal Info */}
                  <InfoRow><label>Phone:</label><value>{reg.phoneNumber || 'N/A'}</value></InfoRow>
                  <InfoRow><label>Email:</label><value>{reg.email || 'N/A'}</value></InfoRow>
                  
                  {/* Organization */}
                  {reg.organizationName && <InfoRow><label>Organization:</label><value>{reg.organizationName}</value></InfoRow>}
                  
                  {/* Banking Info */}
                  {(reg.pan || reg.gst) && (
                    <>
                      <InfoRow><label>PAN:</label><value>{reg.pan || 'N/A'}</value></InfoRow>
                      <InfoRow><label>GST:</label><value>{reg.gst || 'N/A'}</value></InfoRow>
                      <InfoRow><label>Bank Acc:</label><value>{reg.bankAccountNumber || 'N/A'}</value></InfoRow>
                      <InfoRow><label>IFSC:</label><value>{reg.ifscCode || 'N/A'}</value></InfoRow>
                    </>
                  )}
                  
                  {/* Location */}
                  {(reg.state || reg.district || reg.region) && (
                    <>
                      <InfoRow><label>State:</label><value>{reg.state || 'N/A'}</value></InfoRow>
                      <InfoRow><label>Region:</label><value>{reg.region || 'N/A'}</value></InfoRow>
                      <InfoRow><label>District:</label><value>{reg.district || 'N/A'}</value></InfoRow>
                      {reg.addressLine1 && <InfoRow><label>Address:</label><value>{reg.addressLine1}</value></InfoRow>}
                      {reg.addressLine2 && <InfoRow><label>Address 2:</label><value>{reg.addressLine2}</value></InfoRow>}
                    </>
                  )}
                  
                  <InfoRow><label>Submitted:</label><value>{new Date(reg.createdAt).toLocaleDateString()}</value></InfoRow>
                </>
              )}

              <ButtonGroup>
                <Button
                  className="approve"
                  disabled={actionLoading === `approve-${reg.registrationId}`}
                  onClick={() => handleApprove(
                    reg.registrationId,
                    activeTab === 'sellers' ? 'seller' : 'buyer'
                  )}
                >
                  {actionLoading === `approve-${reg.registrationId}` ? '⏳ Processing...' : '✓ Approve'}
                </Button>
                <Button
                  className="reject"
                  disabled={actionLoading === `reject-${reg.registrationId}`}
                  onClick={() => handleRejectClick(
                    reg.registrationId,
                    activeTab === 'sellers' ? 'seller' : 'buyer'
                  )}
                >
                  {actionLoading === `reject-${reg.registrationId}` ? '⏳ Processing...' : '✕ Reject'}
                </Button>
              </ButtonGroup>
            </Card>
          ))}
        </CardGrid>
      )}

      {rejectModal.open && (
        <Modal onClick={() => setRejectModal({ open: false, id: null, type: null })}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <h2>❌ Reject Registration</h2>
            <p>Please provide a reason for rejection:</p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Enter rejection reason..."
            />
            <div className="actions">
              <button 
                className="confirm" 
                onClick={handleRejectConfirm}
                disabled={actionLoading === `reject-${rejectModal.id}`}
              >
                {actionLoading === `reject-${rejectModal.id}` ? '⏳ Processing...' : 'Reject'}
              </button>
              <button 
                className="cancel" 
                onClick={() => setRejectModal({ open: false, id: null, type: null })}
                disabled={actionLoading === `reject-${rejectModal.id}`}
              >
                Cancel
              </button>
            </div>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

export default AdminRegistrationApprovals;
